import { Datetime } from "@/scalars";
import { formatInt, isISOString, isNumericString } from "@/utils/string-utils";
import { timestampToDate } from "@/utils/proto-utils";
import { Timestamp } from "@bufbuild/protobuf/wkt";
import dayjs, { Dayjs } from "dayjs";
import dayjsCalendar from "dayjs/plugin/calendar";
import dayjsDuration from "dayjs/plugin/duration";
import dayjsLocalized from "dayjs/plugin/localizedFormat";
import dayjsRelativeTime from "dayjs/plugin/relativeTime";
import dayjsUpdateLocale from "dayjs/plugin/updateLocale";
import dayjsUtc from "dayjs/plugin/utc";
import { capitalize, first, last } from "lodash";
import { OBSERVED_AT_BOUND_DELTA_PREFIX } from "@/components/query/offline/OfflineQueryUtils";

dayjs.extend(dayjsCalendar);
dayjs.extend(dayjsLocalized);
dayjs.extend(dayjsRelativeTime);
dayjs.extend(dayjsDuration);
dayjs.extend(dayjsUtc);
dayjs.extend(dayjsUpdateLocale);

dayjs.updateLocale("en", {
  relativeTime: {
    future: "in %s",
    past: "%s ago",
    s: "a few seconds",
    m: "1 minute",
    mm: "%d minutes",
    h: "1 hour",
    hh: "%d hours",
    d: "a day",
    dd: "%d days",
    M: "a month",
    MM: "%d months",
    y: "a year",
    yy: "%d years",
  },
});

export const dayjsExtended = dayjs;

export type Instant = string | Date | number | Dayjs;

export const MINUTE_IN_SEC = 60;
export const HOUR_IN_SEC = MINUTE_IN_SEC * 60;
export const DAY_IN_SEC = HOUR_IN_SEC * 24;

export const nowISO = () => dayjs().toISOString();

export function logsFormatDatetime(timestamp: Datetime | number | Date, opts?: { utc?: boolean }) {
  const d = opts?.utc ? dayjs(timestamp).utc() : dayjs(timestamp);
  return d.format("MMM D HH:mm:ss.SSS");
}

type TimePrecision = "seconds" | "millis" | "micros" | "nanos";

export function logFormatDatetimeWithCustomPrecision(
  timestamp: Timestamp | null | undefined,
  precision: TimePrecision,
  opts?: { utc?: boolean },
) {
  const [millis, nanos] = timestampOrNow(timestamp);

  const d = opts?.utc ? dayjs(millis).utc() : dayjs(millis);

  // Base format without fractional seconds
  const baseFormat = d.format("MMM D HH:mm:ss");

  if (precision === "seconds") {
    return baseFormat;
  }

  // Extract milliseconds
  const millisPart = String(Math.floor(nanos / 1_000_000)).padStart(3, "0");

  if (precision === "millis") {
    return `${baseFormat}.${millisPart}`;
  }

  // Extract microseconds (3 digits after milliseconds)
  const microPart = String(Math.floor((nanos % 1_000_000) / 1000)).padStart(3, "0");

  if (precision === "micros") {
    return `${baseFormat}.${millisPart}${microPart}`;
  }

  // Extract nanoseconds (3 digits after microseconds)
  const nanoPart = String(nanos % 1000).padStart(3, "0");

  return `${baseFormat}.${millisPart}${microPart}${nanoPart}`;
}

export function timestampOrNow(timestamp: Timestamp | null | undefined) {
  let millis: number;
  let nanos: number;

  if (timestamp == null) {
    // Use current time
    const now = Date.now();
    millis = now;
    nanos = (now % 1000) * 1_000_000; // Convert milliseconds to nanoseconds
  } else {
    // Convert Timestamp to milliseconds for dayjs
    millis = Number(timestamp.seconds) * 1000 + Math.floor(timestamp.nanos / 1_000_000);
    nanos = timestamp.nanos;
  }

  return [millis, nanos];
}

/** The unit of an integer epoch offset, e.g. Arrow's timestamp resolutions. */
export type EpochTimeUnit = "seconds" | "millis" | "micros" | "nanos";

const EPOCH_UNIT_SCALE: Record<EpochTimeUnit, { perSecond: bigint; fractionDigits: number }> = {
  seconds: { perSecond: BigInt(1), fractionDigits: 0 },
  millis: { perSecond: BigInt("1000"), fractionDigits: 3 },
  micros: { perSecond: BigInt("1000000"), fractionDigits: 6 },
  nanos: { perSecond: BigInt("1000000000"), fractionDigits: 9 },
};

/**
 * Renders an integer epoch offset as ISO 8601, keeping every sub-second digit its
 * unit carries. `Date` holds whole millis, so the calendar part comes from `Date`
 * at second granularity and the fraction is appended from the integer.
 */
export function epochIntegerToISOString(value: bigint, unit: EpochTimeUnit): string {
  const { perSecond, fractionDigits } = EPOCH_UNIT_SCALE[unit];

  // Floor-divide, not truncate: -1µs is one second before the epoch plus 999999µs.
  let seconds = value / perSecond;
  let fraction = value % perSecond;
  if (fraction < BigInt(0)) {
    seconds -= BigInt(1);
    fraction += perSecond;
  }

  const wholeSeconds = new Date(Number(seconds) * 1000);
  if (Number.isNaN(wholeSeconds.getTime())) {
    // Outside Date's range; show the raw integer instead of throwing from a render.
    return value.toString();
  }
  const iso = wholeSeconds.toISOString();
  if (fractionDigits === 0) {
    return iso;
  }
  // toISOString always emits exactly three fractional digits, all zero here.
  return `${iso.slice(0, -".000Z".length)}.${fraction.toString().padStart(fractionDigits, "0")}Z`;
}

export function logsFormatDatetimeFull(
  timestamp: Datetime | number | Date,
  opts?: { utc?: boolean },
) {
  const d = opts?.utc ? dayjs(timestamp).utc() : dayjs(timestamp);
  return d.format("MMM D, YYYY HH:mm:ss.SSS");
}

/** Long-form event timestamp, e.g. "Jul 16, 2026 at 5:41:12.335 pm". */
export function logsFormatDatetimeAt(
  timestamp: Datetime | number | Date,
  opts?: { utc?: boolean },
) {
  const d = opts?.utc ? dayjs(timestamp).utc() : dayjs(timestamp);
  return d.format("MMM D, YYYY [at] h:mm:ss.SSS a");
}

interface PrettyFormatDateOpts {
  caps?: boolean;
  includeAt?: boolean;
  includeWeekday?: boolean;
  seconds?: boolean;
  fullMonthName?: boolean;
}

export function prettyFormatDateForTheBlog(instant: Instant) {
  return dayjs.utc(instant).calendar(null, {
    nextWeek: "LL",
    lastWeek: "LL",
    sameElse: "LL",
    nextDay: "LL",
    lastDay: "LL",
    sameDay: "LL",
  });
}

export function prettyFormatDate(
  instant: Instant,
  { caps = true, seconds = false, fullMonthName = false }: PrettyFormatDateOpts = {},
): string {
  const ts = seconds ? "LTS" : "LT";
  const generic = fullMonthName ? "LL" : "ll";

  return dayjs(instant).calendar(null, {
    nextWeek: generic,
    lastWeek: generic,
    sameElse: generic,
    nextDay: caps ? `[Tomorrow at] ${ts}` : `[tomorrow at] ${ts}`,
    lastDay: caps ? `[Yesterday at] ${ts}` : `[yesterday at] ${ts}`,
    sameDay: caps ? `[Today at] ${ts}` : `[today at] ${ts}`,
  });
}

export function formatDatetimeExplicit(instant: Instant): string {
  return dayjs(instant).format("DD/MM/YY HH:mm:ssZ[Z]");
}

export function parseDurationString(duration: string): dayjsDuration.Duration {
  const heck = `PT${duration.toUpperCase()}`;
  return dayjs.duration(heck);
}

export function formatDatetimeExplicitV2(timestamp: Datetime, opts?: { millis?: boolean }) {
  const numTimestamp = Number(timestamp);
  const parsedTimestamp = isNaN(numTimestamp) ? timestamp : numTimestamp;

  const date = dayjs.utc(parsedTimestamp);

  if (!date.isValid()) {
    console.error("Day.js could not parse timestamp:", timestamp);
    return "Invalid Date";
  }

  if (opts?.millis) {
    return date.format("YYYY-MM-DD HH:mm:ss.SSS[Z]");
  } else {
    return date.format("YYYY-MM-DD HH:mm:ss[Z]");
  }
}

// Formats an absolute instant in a specific IANA timezone (e.g. a notebook's
// configured display timezone). Falls back to the viewer's local zone when the
// timezone is empty/invalid. Uses the native Intl API — no extra dayjs plugin.
export function formatTimestampInTimezone(
  value: Date | string | number | null | undefined,
  timezone?: string,
): string {
  if (value == null) {
    return "";
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const zone = timezone != null && timezone.length > 0 ? timezone : undefined;
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZoneName: "short",
    }).format(date);
  } catch {
    // Invalid timeZone value → fall back to the viewer's local zone.
    return date.toLocaleString();
  }
}

export function prettyFormatTimestampExplicit(
  timestamp: Timestamp | null | undefined,
  opts?: { emptyText?: string; millis?: boolean },
): string {
  if (timestamp == null) {
    return opts?.emptyText ?? "[empty]";
  }

  return formatDatetimeExplicitV2(timestampToDatetime(timestamp), { millis: opts?.millis });
}

export function timestampToDatetime(timestamp: Timestamp): Datetime {
  return timestampToDate(timestamp).toISOString();
}

export function bigintTimestampToDatetime(timestamp: bigint): Datetime {
  return new Date(parseInt(timestamp.toString()) / 1000).toISOString();
}

export function prettyFormatDateTime(
  instant: Instant | Datetime,
  {
    caps = true,
    includeAt = true,
    includeWeekday = false,
    seconds = false,
  }: PrettyFormatDateOpts = {},
): string {
  const ts = seconds ? "LTS" : "LT";
  const at = includeAt ? " at" : "";
  const localInstant = dayjs.utc(instant).local();

  if (includeWeekday) {
    return localInstant.format(`ddd MMM D, YYYY ${ts}`);
  }

  return localInstant.calendar(null, {
    nextWeek: "lll",
    lastWeek: "lll",
    sameElse: "lll",
    nextDay: caps ? `[Tomorrow${at}] ${ts}` : `[tomorrow${at}] ${ts}`,
    lastDay: caps ? `[Yesterday${at}] ${ts}` : `[yesterday${at}] ${ts}`,
    sameDay: caps ? `[Today${at}] ${ts}` : `[today${at}] ${ts}`,
  });
}

export function prettyFormatTime(instant: Instant): string {
  return dayjs(instant).format("LT");
}

/** "16:56" wall-clock time in UTC. Callers append the "UTC" suffix themselves. */
export function prettyFormatTimeUTC(instant: Instant): string {
  return dayjs.utc(instant).format("HH:mm");
}

export function prettyFormatTimeSeconds(instant: Instant): string {
  return dayjs(instant).format("LTS");
}

export function prettyFormatDuration(
  from: Instant,
  to: Instant = dayjs(),
  withSuffix = false,
): string {
  return dayjs.utc(from).to(to, !withSuffix);
}

const YEAR = 60 * 60 * 24 * 365;

export const CHALK_DURATION_REGEX = /^(-)?(\d+y)?(\d+d)?(\d+h)?(\d+m)?(\d+s)?$/;

const durationRegex = /(\d+)([ydhms])/g;

export function parseChalkDuration(duration: string): dayjsDuration.Duration {
  const timeUnits: Record<string, number> = {
    y: 60 * 60 * 24 * 365, // years
    d: 60 * 60 * 24, // days
    h: 60 * 60, // hours
    m: 60, // minutes
    s: 1, // seconds
  };

  let totalSeconds = 0;
  let match: RegExpExecArray | null;

  // Use regex to find all matches in the duration string
  while ((match = durationRegex.exec(duration)) !== null) {
    const value = parseInt(match[1], 10); // Extract the numeric value
    const unit = match[2]; // Extract the time unit
    totalSeconds += value * (timeUnits[unit] || 0); // Convert to seconds and add to total
  }

  return dayjs.duration({ seconds: totalSeconds });
}

export function isValidChalkDuration(s: string): boolean {
  return (
    s.length > 0 &&
    s.startsWith(OBSERVED_AT_BOUND_DELTA_PREFIX) &&
    CHALK_DURATION_REGEX.test(s.substring(OBSERVED_AT_BOUND_DELTA_PREFIX.length))
  );
}

export function isValidTimestamp(value: string): boolean {
  if (typeof value !== "string") return false;

  const match =
    /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})T(?<hour>\d{2}):(?<minute>\d{2}):(?<second>\d{2})(?:\.(?<fraction>\d{3,6}))?\+00:00$/.exec(
      value,
    );
  if (!match?.groups) return false;

  const { year, month, day, hour, minute, second, fraction } = match.groups;
  const normalizedTimestamp =
    `${year}-${month}-${day}T${hour}:${minute}:${second}` +
    (fraction == null ? "" : `.${fraction.slice(0, 3)}`) +
    "+00:00";
  const date = new Date(normalizedTimestamp);
  if (Number.isNaN(date.getTime())) return false;

  return (
    date.getUTCFullYear() === Number.parseInt(year, 10) &&
    date.getUTCMonth() + 1 === Number.parseInt(month, 10) &&
    date.getUTCDate() === Number.parseInt(day, 10) &&
    date.getUTCHours() === Number.parseInt(hour, 10) &&
    date.getUTCMinutes() === Number.parseInt(minute, 10) &&
    date.getUTCSeconds() === Number.parseInt(second, 10) &&
    date.getUTCMilliseconds() === (fraction == null ? 0 : Number.parseInt(fraction.slice(0, 3), 10))
  );
}

// Comparator for two valid ISO timestamps. Assumes inputs are sanitized by the function above.
export function compareTimestamps(left: string, right: string): number {
  return new Date(left).getTime() - new Date(right).getTime();
}

export function prettyFormatDurationSecondsWithThreshold(
  seconds: number | "infinity" | "all",
  thresholdMS: number,
): string {
  if (
    seconds === "infinity" ||
    seconds === "all" ||
    seconds > thresholdMS / 1000 ||
    seconds < 0.001
  ) {
    return prettyFormatDurationSeconds(seconds);
  } else {
    return formatInt(Math.trunc(seconds * 1000)) + "ms";
  }
}

export function shortFormatDurationSecondsAgoDatetime(datetime: Datetime): string {
  const currentNow = new Date();

  const diff = secondsBetweenDatetimes(datetime, currentNow.toISOString());

  const output = prettyFormatDurationSeconds(diff);
  return diff < 600 ? output.split(" ").slice(0, 2).join(" ") : first(output.split(" ")) ?? "";
}

export function prettyFormatDurationSecondsHighPrecision(seconds: number): string {
  if (seconds === 0) return "0s";
  if (seconds < 1e-9) {
    return "<1ns";
  }
  if (seconds < 1e-6) {
    return `${Math.trunc(seconds * 1e9)}ns`;
  }
  if (seconds < 1e-5) {
    return `${(seconds * 1e6).toFixed(2)}µs`;
  }
  if (seconds < 1e-3) {
    return `${Math.trunc(seconds * 1e6)}µs`;
  }
  if (seconds < 0.01) {
    return `${(seconds * 1e3).toFixed(2)}ms`;
  }
  return prettyFormatDurationSeconds(seconds);
}

export function prettyFormatDurationSeconds(seconds: number | "infinity" | "all"): string {
  // More than 100 years means 'infinite' for us.
  if (seconds === "infinity" || seconds === "all" || seconds > 3153600000) {
    return "∞";
  }
  if (seconds === 0) {
    return "0s";
  }
  if (typeof seconds === "number" && seconds < 0) {
    return `-${prettyFormatDurationSeconds(-seconds)}`;
  }
  if (seconds < 0.001) {
    return "<1ms";
  }
  const dur = dayjs.duration(seconds, "seconds");

  let base = "";
  if (seconds < 1) {
    base = dur.format("SSS[ms]");
  } else if (seconds <= YEAR) {
    base = dur.format("Y[y] M[mo] D[d] H[h] m[m] s[s] SSS[ms]");
  } else {
    base = dur.format("Y[y]");
  }
  return (
    base
      // delete all instances of "0x"
      .replace(/\b0+[a-z]+\s*/gi, "")
      // dayjs duration turns 30d intervals into months - turn them back into days
      .replace(/([0-9]+)mo/gi, (_, capture0) => parseInt(capture0) * 30 + "d")
      // if we had months (transformed to days) and other days, combine them into just days
      .replace(
        /([0-9]+)d ([0-9]+)d/gi,
        (_, capture0, capture1) => parseInt(capture0) + parseInt(capture1) + "d",
      )
      // delete leading zeros from every magnitude, e.g. 10s 048ms
      .replace(/(^|\s)0+(?=\d)/g, "$1")
      // delete decimals e.g. 4.49
      .replace(/\.[0-9]+/, "")
      .trim()
  );
}

// A still-running operation only knows its elapsed time to ~1s resolution (it
// ticks once per second), so floor to whole seconds to drop spurious ms.
export function prettyFormatLiveDurationSeconds(seconds: number): string {
  return prettyFormatDurationSeconds(Math.floor(seconds));
}

export function prettyFormatAgo(instant: Instant, customWord?: string): string {
  const customAgo = customWord != null ? ` ${customWord}` : "";
  return dayjs.utc(instant).fromNow(!!customWord) + customAgo;
}

/**
 * Just like prettyFormatAgo, but will render the explicit date past the given
 * threshold number of days
 */
export function prettyFormatAgoWithThreshold(
  instant: Instant,
  thresholdDays: number,
  { caps = true }: { caps?: boolean } = {},
): string {
  const ago = dayjs(instant);
  const now = dayjs();
  const difference = now.diff(ago, "days");
  if (difference > thresholdDays) {
    return prettyFormatDateTime(instant);
  } else {
    const out = prettyFormatAgo(instant);
    return caps ? capitalize(out) : out;
  }
}

export function secondsBetweenDatetimes(a: Datetime, b: Datetime): number {
  const aDate = new Date(a);
  const bDate = new Date(b);
  return Math.abs(bDate.getTime() - aDate.getTime()) / 1e3;
}

export function secondsAgoDatetime(a: Datetime): number {
  const aDate = new Date(a);
  const now = new Date();
  return Math.abs(now.getTime() - aDate.getTime()) / 1e3;
}

const clampBrackets = [
  1,
  5,
  10,
  30,
  60,
  60 * 2, // minutes
  60 * 5,
  60 * 10,
  60 * 15,
  60 * 30,
  60 * 60, // hours
  60 * 60 * 2,
  60 * 60 * 12,
  60 * 60 * 24,
] as const;

/**
 * Clamp the input value to one of the values in the clamp brackets array
 */
export const clampChartWindow = (windowSeconds: number): number => {
  const largerIndex = clampBrackets.findIndex((n) => n > windowSeconds);
  // out of bounds
  if (largerIndex === -1) {
    return last(clampBrackets)!;
  } else if (largerIndex === 0) {
    return first(clampBrackets)!;
  }

  const larger = clampBrackets[largerIndex];
  const smaller = clampBrackets[largerIndex - 1];
  const toSmaller = windowSeconds - smaller;
  const toLarger = larger - windowSeconds;

  return toLarger > toSmaller ? smaller : larger;
};

export const getStartOfMonthAgoFromDate = (date: Date, monthsAgo: number): Date => {
  const monthIndex = date.getMonth();
  const year = date.getFullYear();
  return new Date(Date.UTC(year, monthIndex - monthsAgo, 1, 0, 0, 0, 0));
};

export const getDayStartForDateUTC = (date: Date): Date =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    (-1 * date.getTimezoneOffset()) / 60,
    0,
    0,
    0,
  );

export const getFirstDayOfMonthOfDate = (date: Date) => {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    1,
    (-1 * date.getTimezoneOffset()) / 60,
    0,
    0,
    0,
  );
};

export const getLastDayOfMonthOfDate = (date: Date) => {
  return new Date(
    date.getFullYear(),
    date.getMonth() + 1,
    1,
    (-1 * date.getTimezoneOffset()) / 60,
    0,
    -1,
  );
};

/**
 * Day of month but in UTC because the BE operates in UTC and discrepancies are KILLER
 */
export const getFirstDayOfCurrentMonthUTC = () => {
  return getFirstDayOfMonthOfDate(new Date());
};

/** Start of the current year in UTC, the year-scale counterpart of the month helper above. */
export const getFirstDayOfCurrentYearUTC = () => {
  return new Date(Date.UTC(new Date().getFullYear(), 0, 1));
};

/**
 * A `Date.getTimezoneOffset()` value as a UTC offset label, e.g. `UTC-04:00`. Takes the offset
 * rather than reading it, so the sign convention (positive west of UTC) is inverted in one place.
 */
export const utcOffsetLabel = (offsetMinutes: number): string => {
  const fromUtc = -offsetMinutes;
  const abs = Math.abs(fromUtc);
  const hours = String(Math.floor(abs / 60)).padStart(2, "0");
  const minutes = String(abs % 60).padStart(2, "0");
  return `UTC${fromUtc < 0 ? "-" : "+"}${hours}:${minutes}`;
};

/**
 * Day of month but in UTC because the BE operates in UTC and discrepancies are KILLER
 */
export const getLastDayOfCurrentMonthUTC = () => {
  return getLastDayOfMonthOfDate(new Date());
};

export const getEquivalentUTC = (stamp: number): number => {
  const dateForStamp = new Date(stamp * 1e3);
  return stamp + dateForStamp.getTimezoneOffset() * 60;
};

export const compareDatesNullable = (d1: Date | undefined, d2: Date | undefined): number => {
  if (d1 != null && d2 != null) {
    return compareDatesDefined(d1, d2);
  } else if (d1 == null) {
    return 1;
  } else if (d2 == null) {
    return -1;
  } else {
    return 0;
  }
};

export const compareDatesDefined = (d1: Date, d2: Date): number => {
  const time1 = d1.getTime();
  const time2 = d2.getTime();
  return time1 - time2;
};

export const compareDatetimesNullable = (
  d1: Datetime | null | undefined,
  d2: Datetime | null | undefined,
): number => {
  if (d1 != null && d2 != null) {
    return compareDatetimesDefined(d1, d2);
  } else if (d1 == null) {
    return 1;
  } else if (d2 == null) {
    return -1;
  } else {
    return 0;
  }
};

export const compareDatetimesDefined = (d1: Datetime, d2: Datetime): number => {
  const time1 = new Date(d1).getTime();
  const time2 = new Date(d2).getTime();
  return time1 - time2;
};

type DateParsers = (rawTimestamp: string) => Date | null;

export const parseUnixMillisMaybe = (rawTimestamp: string): Date | null => {
  if (isNumericString(rawTimestamp)) {
    return new Date(Number(rawTimestamp));
  }

  return null;
};

export const parseISOTimestampMaybe = (rawTimestamp: string): Date | null => {
  // Sometimes when coming from a url, the encoded + is messed up.
  const fixedRawTimestamp = rawTimestamp.replace(" 00:00", "+00:00");
  if (isISOString(fixedRawTimestamp)) {
    return new Date(fixedRawTimestamp);
  }

  return null;
};

const defaultParsers: DateParsers[] = [parseUnixMillisMaybe, parseISOTimestampMaybe];
/**
 * Handles parsing raw timestamps of multiple formats. By default, attempts parsing UNIX Millisecond Timestamps
 * and ISO strings
 *
 * @param rawTimestamp a string representing a time, maybe
 * @param parsers a list of functions that check for a specific date format and return a Date if valid, null if not.
 *                Parsers are run in array order.
 * @returns a Date matching one of the parser's expected format, or null if none
 */
export const parseRawTimestamp = (rawTimestamp: string, parsers = defaultParsers): Date | null => {
  for (const parser of parsers) {
    const maybeDate = parser(rawTimestamp);
    if (maybeDate != null && maybeDate.toString() !== "Invalid Date") {
      return maybeDate;
    }
  }

  return null;
};

export const endOfMinute = (date: Date): Date => {
  if (date.getSeconds() !== 0 || date.getMilliseconds() !== 0) {
    return date;
  }

  const newEndDate = new Date(date);
  newEndDate.setSeconds(59, 999);
  return newEndDate;
};

// Converts API duration strings such as "1h30m0s" to milliseconds.
export const parseDurationMs = (duration: string): number => {
  const units: Record<string, number> = {
    h: 3_600_000,
    m: 60_000,
    s: 1_000,
    ms: 1,
    us: 0.001,
    µs: 0.001,
    μs: 0.001,
    ns: 0.000001,
  };
  return Array.from(duration.matchAll(/(\d+(?:\.\d*)?|\.\d+)(h|ms|m|s|us|µs|μs|ns)/g)).reduce(
    (total, match) => total + Number(match[1]) * units[match[2]],
    0,
  );
};
